import React, {useState, useEffect, useRef, useReducer, useMemo} from "react";
import {useLocation} from "react-router-dom";
import {verifyUser, jwtDecode} from "../../authRequests";
import {crud} from "../../crudRequests";
import {patientReducer, init} from "../../additional_files/reducer";
import Layout from "../Layout";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Switch,
  Text,
  Textarea,
  VStack,
  SimpleGrid,
  Spacer,
  HStack,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import {useToast} from "@chakra-ui/react";
import DateStrip, {dateToString} from "../DateStrip";
import {AddIcon} from "@chakra-ui/icons";
import moment from "moment";

const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  const day = now.getDate().toString().padStart(2, "0");
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

function isoToDatetimeLocal(isoDate) {
  try {
    const date = new Date(isoDate);

    // Convert date to YYYY-MM-DDTHH:mm format
    const formattedDate = date.toISOString().slice(0, 16);

    return formattedDate;
  } catch (e) {
    return "";
  }
}

function datetimeLocalToISO(datetimeLocal) {
  try {
    const date = new Date(datetimeLocal);

    // Convert date to ISO format
    const isoDate = date.toISOString();

    return isoDate;
  } catch (e) {
    return "";
  }
}

// Helpers to make time entry faster
function nowDatetimeLocal() {
  try {
    return new Date().toISOString().slice(0, 16);
  } catch (e) {
    return "";
  }
}

function shiftMinutesStr(datetimeLocal, deltaMinutes) {
  try {
    const base =
      datetimeLocal && datetimeLocal.length
        ? new Date(datetimeLocal)
        : new Date();
    base.setMinutes(base.getMinutes() + deltaMinutes);
    return base.toISOString().slice(0, 16);
  } catch (e) {
    return datetimeLocal;
  }
}

// Combine selected day (Date) and a HH:mm string into ISO
function combineDateAndTime(dayDateObj, hhmm) {
  try {
    if (!dayDateObj || !hhmm) return "";
    const y = dayDateObj.getFullYear();
    const m = String(dayDateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dayDateObj.getDate()).padStart(2, "0");
    const base = new Date(`${y}-${m}-${d}T${hhmm}`);
    return base.toISOString();
  } catch (_) {
    return "";
  }
}

function minutesBetweenISO(a, b) {
  try {
    const ms = new Date(b) - new Date(a);
    return Math.max(0, Math.round(ms / 60000));
  } catch (_) {
    return 0;
  }
}

function minutesBetweenLocal(aLocal, bLocal) {
  try {
    if (!aLocal || !bLocal) return 0;
    const ms = new Date(aLocal) - new Date(bLocal);
    return Math.abs(Math.round(ms / 60000));
  } catch (_) {
    return 0;
  }
}

function formatHm(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h ${r}m`;
}

const ActivityMapper = ({factor, getData, updateData, setData}) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (getData("activities") === undefined) return;
    if (
      getData("activities").filter((item) => item.type === factor.name)
        ?.length > 0 &&
      !enabled
    ) {
      setEnabled(true);
    }
  }, [getData("activities")]);

  useEffect(() => {
    setEnabled(false);
  }, [getData("day")]);

  const toast = useToast();

  const updateActivity = (index, newData) => {
    setData((prev) => {
      let newActivities = [...prev.activities];
      const updatedObj = {...newActivities[index], ...newData};
      newActivities[index] = updatedObj;
      if (
        newActivities[index]?.start_time === null ||
        newActivities[index]?.end_time === null
      ) {
        return;
      }

      if (moment(updatedObj.start_time).isAfter(moment(updatedObj.end_time))) {
        toast({
          title: "Invalid time",
          description: "Start time cannot be after end time",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      return {...prev, activities: newActivities};
    });
  };

  const currActivity = {
    start_time: getCurrentDateTime(),
    end_time: getCurrentDateTime(),
    type: factor?.name,
  };

  const handleToggle = () => {
    if (enabled) {
      const otherActivities = getData("activities").filter(
        (item) => item.type !== factor.name
      );
      // remove this activity when the toggle is disabled
      updateData("activities")([...otherActivities]);
    } else {
      // add the activity to the list
      updateData("activities")([...getData("activities"), currActivity]);
    }
    setEnabled((prev) => !prev);
  };

  return (
    <Box w="100%" maxW="full" py={2}>
      <Flex align="center" justify="space-between">
        <FormLabel m={0} fontSize="sm" color="gray.700">
          {factor.label}
        </FormLabel>
        <Switch onChange={handleToggle} isChecked={enabled} colorScheme="purple" size="md" />
      </Flex>
      {enabled && (
        <Box mt={3} pl={1}>
          {getData("activities")?.map((item, i) => {
            if (item.type !== factor.name) return null;
            return (
              <Flex key={i} gap={2} mb={2} align="center" flexWrap="wrap">
                <Input
                  width="170px"
                  size="sm"
                  type="datetime-local"
                  placeholder="Start"
                  borderColor="gray.200"
                  _hover={{borderColor: "gray.300"}}
                  _focus={{borderColor: "#5754FF", boxShadow: "0 0 0 1px #5754FF"}}
                  onChange={(e) =>
                    updateActivity(i, {start_time: e.target.value})
                  }
                  value={item?.start_time}
                />
                <Text color="gray.400" fontSize="xs">to</Text>
                <Input
                  width="170px"
                  size="sm"
                  type="datetime-local"
                  placeholder="End"
                  borderColor="gray.200"
                  _hover={{borderColor: "gray.300"}}
                  _focus={{borderColor: "#5754FF", boxShadow: "0 0 0 1px #5754FF"}}
                  onChange={(e) =>
                    updateActivity(i, {end_time: e.target.value})
                  }
                  value={item?.end_time}
                />
                <HStack spacing={1}>
                  <Button
                    size="xs"
                    variant="outline"
                    borderColor="gray.200"
                    color="gray.600"
                    fontWeight="normal"
                    _hover={{bg: "gray.50"}}
                    onClick={() =>
                      updateActivity(i, {
                        start_time: nowDatetimeLocal(),
                        end_time: nowDatetimeLocal(),
                      })
                    }
                  >
                    Now
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="gray.500"
                    fontWeight="normal"
                    _hover={{bg: "gray.50"}}
                    onClick={() =>
                      updateActivity(i, {
                        start_time: shiftMinutesStr(item?.start_time, -15),
                      })
                    }
                  >
                    -15m
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="gray.500"
                    fontWeight="normal"
                    _hover={{bg: "gray.50"}}
                    onClick={() =>
                      updateActivity(i, {
                        end_time: shiftMinutesStr(item?.end_time, 15),
                      })
                    }
                  >
                    +15m
                  </Button>
                </HStack>
              </Flex>
            );
          })}
          <Button
            size="xs"
            variant="ghost"
            color="#5754FF"
            fontWeight="500"
            _hover={{bg: "#f0f0ff"}}
            onClick={() =>
              updateData("activities")([...getData("activities"), currActivity])
            }
          >
            <AddIcon w="8px" mr={1.5} /> Add interval
          </Button>
        </Box>
      )}
    </Box>
  );
};

const useFormData = (initialData) => {
  const [data, setData] = useState(initialData);

  const updateData = (name) => (value) => {
    setData((prev) => ({...prev, [name]: value}));
  };

  const getData = (name) => {
    return data[name];
  };

  return [getData, updateData, data, setData];
};

export default function SleepDiary(props) {
  const {state} = useLocation();
  const [dashState, dispatch] = useReducer(patientReducer, state, init);
  const toast = useToast();
  const selectedAppointment = useRef(null);
  selectedAppointment.current = dashState.selectedAppointment;

  let room = dashState.patient?.pid;

  const loadedRef = useRef(null);
  const id = state && jwtDecode(state.jwt).id;

  const [validDates, setValidDates] = useState([new Date()]);
  const [completedDates, setCompletedDates] = useState([]);

  const queryValidDates = (id) => {
    crud(state, [
      {
        db: dashState?.db || "blh",
        collection: "sleep_diary",
        parameters: [{pid: id}],
        method: "find",
      },
    ]).then((res) => {
      const list = Array.isArray(res?.data?.[0])
        ? res.data[0]
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const dates = list.map((entry) => entry?.day).filter(Boolean);
      setCompletedDates(dates);
      setValidDates([...dates, dateToString(new Date())]);
    });
  };

  useEffect(() => {
    verifyUser(state ? state.jwt : "", loadedRef);

    const date = new Date();
    updateData("day")(date);
    // Set typical defaults on first mount
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const toLocal = (hhmm) => `${y}-${m}-${d}T${hhmm}`;
    updateData("time_inbed")(toLocal("22:30"));
    updateData("time_asleep")(toLocal("23:00"));
    updateData("time_awake")(toLocal("06:30"));
    updateData("time_outofbed")(toLocal("06:45"));

    queryValidDates(id);
  }, []);

  const [getData, updateData, data, setData] = useFormData({
    day: new Date(),
    time_inbed: null,
    time_asleep: null,
    time_awake: null,
    time_outofbed: null,
    dayType: "work",
    activities: [],
    additionalInfo: "",
  });

  const upsertDiary = async (id, date) => {
    // Save in local datetime-local strings to avoid timezone drift
    const isoData = {...data};

    // Check if the current time is AM or PM
    const currentHour = new Date().getHours();
    let updatedDate = new Date(date);

    // If it's PM, set updatedDate as the next day
    if (currentHour >= 12) {
      updatedDate.setDate(updatedDate.getDate() + 1);
    }

    await crud(state, [
      {
        db: dashState?.db || "blh",
        collection: "sleep_diary",
        parameters: [
          {pid: id, day: date},
          {
            $set: {
              ...data,
              day: date,
              pid: id,
            },
          },
          {upsert: true},
        ],
        method: "updateOne",
      },
    ]);
  };

  const handleSubmit = () => {
    console.log({data});
    if (
      !getData("time_inbed") ||
      !getData("time_asleep") ||
      !getData("time_awake") ||
      !getData("time_outofbed") ||
      !getData("dayType")
    ) {
      toast({
        title: "Form incomplete",
        description: "Please complete all the required fields",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    const dayStr = dateToString(getData("day"));
    toast.promise(upsertDiary(id, dayStr), {
      success: {title: "Success", description: "Entry recorded"},
      error: {title: "Please try again", description: "Something wrong"},
      loading: {title: "Loading...", description: "Please wait"},
    });
    // immediately reflect completion in the strip
    setCompletedDates((prev) =>
      prev.includes(dayStr) ? prev : [...prev, dayStr]
    );
  };

  const fillTypicalSchedule = () => {
    const day = getData("day");
    const toLocal = (hhmm) => {
      try {
        const y = day.getFullYear();
        const m = String(day.getMonth() + 1).padStart(2, "0");
        const d = String(day.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}T${hhmm}`;
      } catch (_) {
        return "";
      }
    };
    updateData("time_inbed")(toLocal("22:30"));
    updateData("time_asleep")(toLocal("23:00"));
    updateData("time_awake")(toLocal("06:30"));
    updateData("time_outofbed")(toLocal("06:45"));
  };

  const baseFactors = [
    {
      label: "What type of day was it?",
      value: getData("dayType"),
      onChange: (e) => updateData("dayType")(e.target.value),
      type: "select",
      options: ["Work", "Day off", "Sick day", "Vacation", "Other"],
    },
    {
      label: "What time did you get in bed?",
      value: getData("time_inbed"),
      onChange: (e) => updateData("time_inbed")(e.target.value),
      type: "time",
    },
    {
      label: "What time you fall asleep?",
      value: getData("time_asleep"),
      onChange: (e) => updateData("time_asleep")(e.target.value),
      type: "time",
      indent: true,
    },
    {
      label: "When did you wake up?",
      value: getData("time_awake"),
      onChange: (e) => updateData("time_awake")(e.target.value),
      type: "time",
      indent: true,
    },
    {
      label: "When did you get out of bed?",
      value: getData("time_outofbed"),
      onChange: (e) => updateData("time_outofbed")(e.target.value),
      type: "time",
    },
  ];

  const additionalFactors = [
    {
      label: "Did you engage in exercise?",
      name: "exercise",
    },
    {
      label: "Did you take any naps?",
      name: "nap",
    },
    {
      label: "Did you have any awakenings?",
      name: "awakenings",
    },
  ];

  const riskFactors = [
    {
      label: "Did you consume any caffeine?",
      name: "caffeine",
    },
    {
      label: "Did you consume any alcohol?",
      name: "alchohol",
    },
    {
      label: "Did you consume any drugs?",
      name: "drugs",
    },
  ];

  const mapper = (items) => {
    return (
      <SimpleGrid columns={{base: 1, md: 2}} spacing={4} w="100%" maxW="720px">
        {items.map((factor, idx) => (
          <FormControl key={idx} isRequired ml={factor.indent ? 4 : 0}>
            <FormLabel fontSize="sm" color="gray.700">
              {factor.label}
            </FormLabel>
            {factor.type === "time" && (
              <Flex align="center" gap={2}>
                <Input
                  size="sm"
                  value={factor.value}
                  onChange={factor.onChange}
                  placeholder="Select time"
                  type="datetime-local"
                  width="180px"
                />
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() =>
                    factor.onChange({target: {value: nowDatetimeLocal()}})
                  }
                >
                  Now
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    factor.onChange({
                      target: {value: shiftMinutesStr(factor.value, -15)},
                    })
                  }
                >
                  -15m
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    factor.onChange({
                      target: {value: shiftMinutesStr(factor.value, 15)},
                    })
                  }
                >
                  +15m
                </Button>
              </Flex>
            )}
            {factor.type === "select" && (
              <Select
                size="sm"
                onChange={factor.onChange}
                value={factor?.value?.toLowerCase()?.replace(" ", "-")}
                placeholder={factor?.placeholder || "Select"}
              >
                {factor.options.map((option) => (
                  <option
                    key={option}
                    value={option.toLowerCase().replace(" ", "-")}
                  >
                    {option}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>
        ))}
      </SimpleGrid>
    );
  };

  const loadEntry = async (id, date) => {
    // Do not clear defaults; only overwrite if an entry exists
    let res = await crud(state, [
      {
        db: dashState?.db || "blh",
        collection: "sleep_diary",
        parameters: [{pid: id, day: date}],
        method: "findOne",
      },
    ]);
    const entry = res?.data?.[0];
    if (entry) {
      // Store and display times exactly as saved (local time strings)
      updateData("time_inbed")(entry?.time_inbed || "");
      updateData("time_asleep")(entry?.time_asleep || "");
      updateData("time_awake")(entry?.time_awake || "");
      updateData("time_outofbed")(entry?.time_outofbed || "");
      updateData("additionalInfo")(entry?.additionalInfo || "");
      updateData("dayType")(entry?.dayType || "");
      if (Array.isArray(entry?.activities)) {
        updateData("activities")(entry.activities);
      }
    } else {
      // No entry for this day: reset to typical defaults for the selected date
      try {
        const d = new Date(date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const toLocal = (hhmm) => `${y}-${m}-${day}T${hhmm}`;
        updateData("time_inbed")(toLocal("22:30"));
        updateData("time_asleep")(toLocal("23:00"));
        updateData("time_awake")(toLocal("06:30"));
        updateData("time_outofbed")(toLocal("06:45"));
      } catch (_) {
        updateData("time_inbed")("");
        updateData("time_asleep")("");
        updateData("time_awake")("");
        updateData("time_outofbed")("");
      }
      updateData("dayType")("work");
      updateData("additionalInfo")("");
      updateData("activities")([]);
    }
  };

  useEffect(() => {
    loadEntry(id, dateToString(getData("day")));
  }, [id]);

  // Extract HH:mm from a datetime-local string for display in time inputs
  const extractTime = (datetimeLocal) => {
    if (!datetimeLocal) return "";
    return datetimeLocal.slice(11, 16);
  };

  // Combine the selected day with a HH:mm time string to produce a datetime-local string
  const buildDatetime = (hhmm) => {
    try {
      const day = getData("day") || new Date();
      const y = day.getFullYear();
      const m = String(day.getMonth() + 1).padStart(2, "0");
      const d = String(day.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}T${hhmm}`;
    } catch (_) {
      return hhmm;
    }
  };

  const TimeControl = ({factor}) => (
    <FormControl isRequired>
      <FormLabel fontSize="xs" color="gray.500" mb={1.5} fontWeight="500">
        {factor.label}
      </FormLabel>
      <Flex align="center" gap={2}>
        <Input
          size="sm"
          value={extractTime(factor.value)}
          onChange={(e) =>
            factor.onChange({target: {value: buildDatetime(e.target.value)}})
          }
          type="time"
          w="110px"
          bg="white"
          borderColor="gray.200"
          _hover={{borderColor: "gray.300"}}
          _focus={{borderColor: "#5754FF", boxShadow: "0 0 0 1px #5754FF"}}
        />
        <HStack spacing={0}>
          <Button
            size="xs"
            variant="ghost"
            color="gray.400"
            fontWeight="normal"
            px={2}
            _hover={{bg: "gray.50", color: "#5754FF"}}
            onClick={() =>
              factor.onChange({
                target: {value: shiftMinutesStr(factor.value, -15)},
              })
            }
          >
            -15m
          </Button>
          <Button
            size="xs"
            variant="ghost"
            color="gray.400"
            fontWeight="normal"
            px={2}
            _hover={{bg: "gray.50", color: "#5754FF"}}
            onClick={() =>
              factor.onChange({
                target: {value: shiftMinutesStr(factor.value, 15)},
              })
            }
          >
            +15m
          </Button>
        </HStack>
      </Flex>
    </FormControl>
  );

  const sleepMin = minutesBetweenLocal(
    getData("time_awake"),
    getData("time_asleep")
  );
  const bedMin = minutesBetweenLocal(
    getData("time_outofbed"),
    getData("time_inbed")
  );
  const efficiency = bedMin > 0 ? Math.min(100, Math.round((sleepMin / bedMin) * 100)) : 0;

  const todayStr = dateToString(new Date());
  const selectedStr = dateToString(getData("day") || new Date());

  const handleDateSelect = (d, dStr) => {
    updateData("day")(d);
    loadEntry(id, dStr);
  };

  const handleGoToToday = () => {
    const today = new Date();
    updateData("day")(today);
    loadEntry(id, todayStr);
  };

  return (
    <Layout state={{...dashState}} dispatch={dispatch}>
      <VStack
        ref={loadedRef}
        style={{opacity: 0}}
        flex={1}
        w="100%"
        overflow={"auto"}
        alignItems="stretch"
      >
        <Box w="100%" px={{base: 4, md: 6}} py={5}>
          {/* Page header with stats inline */}
          <Flex
            align={{base: "flex-start", md: "center"}}
            justify="space-between"
            mb={5}
            direction={{base: "column", md: "row"}}
            gap={3}
          >
            <Box>
              <Heading fontSize="xl" fontWeight="600" color="#1a1f36">
                Sleep Diary
              </Heading>
              <Text fontSize="13px" color="#6b7280" mt={1}>
                Track your sleep patterns and daily habits
              </Text>
            </Box>
            {/* Inline summary stats */}
            <HStack
              spacing={4}
              bg="white"
              borderWidth="1px"
              borderColor="gray.100"
              rounded="lg"
              px={5}
              py={2.5}
            >
              <Box textAlign="center" minW="60px">
                <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>
                  Asleep
                </Text>
                <Text fontSize="md" fontWeight="600" color="#1a1f36">
                  {formatHm(sleepMin)}
                </Text>
              </Box>
              <Box w="1px" h="28px" bg="gray.100" />
              <Box textAlign="center" minW="60px">
                <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>
                  In bed
                </Text>
                <Text fontSize="md" fontWeight="600" color="#1a1f36">
                  {formatHm(bedMin)}
                </Text>
              </Box>
              <Box w="1px" h="28px" bg="gray.100" />
              <Box textAlign="center" minW="50px">
                <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>
                  Efficiency
                </Text>
                <Text
                  fontSize="md"
                  fontWeight="600"
                  color={efficiency >= 85 ? "green.500" : efficiency >= 70 ? "orange.400" : "#1a1f36"}
                >
                  {efficiency}%
                </Text>
              </Box>
            </HStack>
          </Flex>

          <DateStrip
            activeDate={getData("day") || new Date()}
            onDateSelect={handleDateSelect}
            completedDates={completedDates}
            onGoToToday={handleGoToToday}
          />

          {/* Sleep schedule */}
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.100"
            rounded="lg"
            px={5}
            py={5}
            mb={4}
          >
            <Flex align="center" justify="space-between" mb={4}>
              <Heading as="h3" fontSize="sm" fontWeight="600" color="#1a1f36">
                Sleep schedule
              </Heading>
              <FormControl isRequired w="auto" display="flex" alignItems="center" gap={2}>
                <FormLabel fontSize="xs" color="gray.500" mb={0} whiteSpace="nowrap" fontWeight="normal">
                  Day type
                </FormLabel>
                <Select
                  size="xs"
                  onChange={baseFactors[0].onChange}
                  value={baseFactors[0].value?.toLowerCase?.()?.replace(" ", "-")}
                  placeholder={baseFactors[0].placeholder || "Select"}
                  w="130px"
                  borderColor="gray.200"
                  rounded="md"
                  _hover={{borderColor: "gray.300"}}
                  _focus={{borderColor: "#5754FF", boxShadow: "0 0 0 1px #5754FF"}}
                >
                  {baseFactors[0].options.map((option) => (
                    <option
                      key={option}
                      value={option.toLowerCase().replace(" ", "-")}
                    >
                      {option}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Flex>

            <SimpleGrid columns={{base: 1, md: 2}} spacingY={4} spacingX={6}>
              <Box>
                <Text fontSize="10px" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb={3}>
                  Evening
                </Text>
                <VStack spacing={4} align="stretch">
                  <TimeControl factor={baseFactors[1]} />
                  <TimeControl factor={baseFactors[2]} />
                </VStack>
              </Box>
              <Box>
                <Text fontSize="10px" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb={3}>
                  Morning
                </Text>
                <VStack spacing={4} align="stretch">
                  <TimeControl factor={baseFactors[3]} />
                  <TimeControl factor={baseFactors[4]} />
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Additional & Risk factors + Notes in a 3-col layout on large screens */}
          <SimpleGrid columns={{base: 1, md: 3}} spacing={4} mb={5}>
            <Box
              bg="white"
              borderWidth="1px"
              borderColor="gray.100"
              rounded="lg"
              px={5}
              py={5}
            >
              <Heading as="h3" fontSize="sm" fontWeight="600" mb={3} color="#1a1f36">
                Activities
              </Heading>
              <VStack spacing={0} divider={<Box borderBottomWidth="1px" borderColor="gray.50" w="100%" />}>
                {additionalFactors.map((factor, index) => (
                  <ActivityMapper
                    factor={factor}
                    key={index}
                    getData={getData}
                    updateData={updateData}
                    setData={setData}
                  />
                ))}
              </VStack>
            </Box>
            <Box
              bg="white"
              borderWidth="1px"
              borderColor="gray.100"
              rounded="lg"
              px={5}
              py={5}
            >
              <Heading as="h3" fontSize="sm" fontWeight="600" mb={3} color="#1a1f36">
                Substances
              </Heading>
              <VStack spacing={0} divider={<Box borderBottomWidth="1px" borderColor="gray.50" w="100%" />}>
                {riskFactors.map((factor, index) => (
                  <ActivityMapper
                    factor={factor}
                    key={index}
                    getData={getData}
                    updateData={updateData}
                    setData={setData}
                  />
                ))}
              </VStack>
            </Box>
            <Box
              bg="white"
              borderWidth="1px"
              borderColor="gray.100"
              rounded="lg"
              px={5}
              py={5}
            >
              <Heading as="h3" fontSize="sm" fontWeight="600" mb={3} color="#1a1f36">
                Notes
              </Heading>
              <FormControl>
                <Textarea
                  value={getData("additionalInfo")}
                  onChange={(e) => updateData("additionalInfo")(e.target.value)}
                  placeholder="Anything else about your sleep..."
                  size="sm"
                  bg="white"
                  borderColor="gray.200"
                  _hover={{borderColor: "gray.300"}}
                  _focus={{borderColor: "#5754FF", boxShadow: "0 0 0 1px #5754FF"}}
                  rows={5}
                  resize="none"
                />
              </FormControl>
            </Box>
          </SimpleGrid>

          {/* Submit */}
          <Flex mb={6} align="center" gap={3}>
            <Button
              onClick={handleSubmit}
              bg="#5754FF"
              color="white"
              size="sm"
              px={6}
              fontWeight="500"
              _hover={{bg: "#4644e0"}}
              _active={{bg: "#3a38c8"}}
            >
              Save entry
            </Button>
            {completedDates?.includes?.(selectedStr) && (
              <Text fontSize="xs" color="green.500" fontWeight="500">
                Entry saved for this date
              </Text>
            )}
          </Flex>
        </Box>
      </VStack>
    </Layout>
  );
}
